<script setup lang="ts">
import type { RoleDTO } from '@erpx/api'
import { ECheckbox, EField, ETextarea } from '@erpx/ui'
import { computed } from 'vue'

import AppIcon from '@/components/AppIcon.vue'

/**
 * Role assignment, with the segregation-of-duties conversation built in.
 *
 * §6 forbids combining declared-conflicting permissions unless a tenant admin
 * overrides "with a logged reason". The server is the authority: it answers
 * `ERR_SOD_VIOLATION` with one field error per offending pair. This component
 * renders those pairs verbatim — the conflict is a policy statement, and
 * paraphrasing it in the client would be inventing policy — and then reveals
 * the reason box, because an override without a reason is not an override.
 */

const props = withDefaults(
  defineProps<{
    roles: readonly RoleDTO[]
    /** Selected role codes. */
    modelValue: readonly string[]
    /** Messages from a `ERR_SOD_VIOLATION` problem, one per conflicting pair. */
    conflicts?: readonly string[]
    /** The logged override reason (§6). Bound with `v-model:reason`. */
    reason?: string
    disabled?: boolean
    loading?: boolean
  }>(),
  { conflicts: () => [], reason: '' },
)

const emit = defineEmits<{
  'update:modelValue': [codes: string[]]
  'update:reason': [reason: string]
}>()

const selected = computed(() => new Set(props.modelValue))

const systemRoles = computed(() => props.roles.filter((role) => role.isSystem))
const customRoles = computed(() => props.roles.filter((role) => !role.isSystem))

function toggle(code: string, on: boolean): void {
  const next = new Set(selected.value)
  if (on) next.add(code)
  else next.delete(code)
  emit('update:modelValue', [...next])
}

const reasonModel = computed({
  get: () => props.reason,
  set: (value: string) => emit('update:reason', value),
})
</script>

<template>
  <div class="stack">
    <fieldset class="roles" :disabled="disabled">
      <legend class="roles__legend">{{ $t('users.roleEditor.legend') }}</legend>

      <p v-if="loading" class="muted text-xs">{{ $t('users.roleEditor.loading') }}</p>

      <div v-else class="roles__grid">
        <ECheckbox
          v-for="role in systemRoles"
          :key="role.code"
          :model-value="selected.has(role.code)"
          :label="role.name"
          :description="role.description"
          @update:model-value="toggle(role.code, $event)"
        />
        <ECheckbox
          v-for="role in customRoles"
          :key="role.code"
          :model-value="selected.has(role.code)"
          :label="role.name"
          :description="role.description"
          @update:model-value="toggle(role.code, $event)"
        />
      </div>
    </fieldset>

    <div v-if="conflicts.length > 0" class="alert alert--warning" role="alert">
      <AppIcon name="alert" :size="15" />
      <div class="stack--tight">
        <p class="roles__conflict-title">{{ $t('users.roleEditor.conflictTitle') }}</p>
        <ul class="roles__conflicts">
          <li v-for="(conflict, index) in conflicts" :key="index">{{ conflict }}</li>
        </ul>
        <p class="text-xs">{{ $t('users.roleEditor.conflictNote') }}</p>
      </div>
    </div>

    <EField
      v-if="conflicts.length > 0"
      :label="$t('users.roleEditor.reasonLabel')"
      :hint="$t('users.roleEditor.reasonHint')"
      required
    >
      <ETextarea
        v-model="reasonModel"
        :rows="2"
        autosize
        :placeholder="$t('users.roleEditor.reasonPlaceholder')"
      />
    </EField>
  </div>
</template>

<style>
.roles {
  padding: 0;
  margin: 0;
  border: none;
}

.roles__legend {
  padding: 0;
  margin-block-end: var(--erpx-space-2);
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-2xs);
  font-weight: var(--erpx-weight-semibold);
  letter-spacing: var(--erpx-tracking-wide);
  text-transform: uppercase;
}

.roles__grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: var(--erpx-space-2) var(--erpx-space-4);
  max-height: 16rem;
  overflow-y: auto;
  padding-inline-end: var(--erpx-space-2);
}

.roles__conflict-title {
  margin: 0;
  font-weight: var(--erpx-weight-semibold);
}

.roles__conflicts {
  margin: 0;
  padding-inline-start: var(--erpx-space-4);
  font-size: var(--erpx-text-xs);
}
</style>
